import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import type * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { normalizeParentComponentMode } from './parentComponentMode';

export interface ProductServiceValue {
  product: string;
  service: string;
  parentComponent?: string;
}

export const AsyncProductAndServiceSelect = ({
  onChange,
  value,
  required,
  schema,
  uiSchema,
}: FieldExtensionComponentProps<ProductServiceValue>) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const catalogApi = useApi(catalogApiRef);
  const parentComponentMode = useMemo(
    () => normalizeParentComponentMode(
      (uiSchema?.['ui:options'] as Record<string, unknown>)?.showParentComponent,
    ),
    [uiSchema],
  );
  const showParentComponent = parentComponentMode !== 'hidden';
  const parentComponentRequired = parentComponentMode === 'required';
  const serviceRequired = useMemo(
    () => Array.isArray(schema?.required) && schema.required.includes('service'),
    [schema],
  );

  const [options, setOptions] = useState<{ label: string; value: string }[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>(value?.product || '');
  const [serviceOptions, setServiceOptions] = useState<{ value: string; label: string }[]>([]);
  const [selectedService, setSelectedService] = useState<string>(value?.service || '');
  const [error, setError] = useState<string | null>(null);
  const [serviceError, setServiceError] = useState<string | null>(null);

  const [componentOptions, setComponentOptions] = useState<{ value: string; label: string }[]>([]);
  const [selectedParentComponent, setSelectedParentComponent] = useState<string>(value?.parentComponent || '');
  const [componentLoading, setComponentLoading] = useState(false);
  const [componentError, setComponentError] = useState<string | null>(null);

  const fetchProducts = useCallback(async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('edm-connector');
      const response = await fetchApi.fetch(`${baseUrl}/product`);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch products: ${response.statusText}${errorText && errorText.trim() ? ` (${errorText.trim().slice(0, 200)})` : ''}`
        );
      }

      const products = await response.json();
      const productOptions = Array.isArray(products)
        ? products.map((name: string) => ({ label: name, value: name }))
        : [];

      setOptions(productOptions);
      setError(null);
    } catch (err) {
      setError(`Failed to fetch products. Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [discoveryApi, fetchApi]);

  const fetchServicesForProduct = useCallback(async (productName: string) => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('edm-connector');
      const response = await fetchApi.fetch(`${baseUrl}/services-for-product?productName=${encodeURIComponent(productName)}`);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch services for product ${productName}: ${response.statusText}${errorText && errorText.trim() ? ` (${errorText.trim().slice(0, 200)})` : ''}`
        );
      }

      const services = await response.json();

      if (Array.isArray(services) && services.length === 0) {
        setServiceOptions([]);
        setServiceError(`No services exist for product ${productName}`);
      } else {
        const prefix = `${productName}-`;
        setServiceOptions(
          Array.isArray(services)
            ? services.map((name: string) => ({
                value: name,
                label: name.startsWith(prefix) ? name.slice(prefix.length) : name,
              }))
            : [],
        );
        setServiceError(null);
      }
    } catch (err) {
      setServiceOptions([]);
      setServiceError(`Failed to fetch services for product ${productName}`);
    }
  }, [discoveryApi, fetchApi]);

  const fetchComponentsForService = useCallback(async (serviceName: string) => {
    if (!showParentComponent) return;
    setComponentLoading(true);
    try {
      const response = await catalogApi.getEntities({
        filter: {
          kind: 'Component',
          'spec.system': `system:default/${serviceName}`,
        },
        fields: ['metadata.name', 'metadata.namespace', 'metadata.title', 'kind'],
      });

      setComponentOptions(
        (response.items || []).map((entity: Entity) => ({
          value: stringifyEntityRef(entity),
          label: entity.metadata.title || entity.metadata.name,
        })),
      );
      setComponentError(null);
    } catch {
      setComponentOptions([]);
      setComponentError(`Failed to fetch components for service ${serviceName}`);
    } finally {
      setComponentLoading(false);
    }
  }, [catalogApi, showParentComponent]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    if (selectedProduct) {
      fetchServicesForProduct(selectedProduct);
    } else {
      setServiceOptions([]);
      setServiceError(null);
    }
  }, [selectedProduct, fetchServicesForProduct]);

  useEffect(() => {
    if (!showParentComponent) {
      setComponentOptions([]);
      return;
    }

    if (selectedService) {
      fetchComponentsForService(selectedService);
    } else {
      setComponentOptions([]);
    }
  }, [selectedService, fetchComponentsForService, showParentComponent]);

  const emitChange = useCallback(
    (product: string, service: string, parentComponent: string) => {
      const val: ProductServiceValue = { product, service };
      if (showParentComponent && parentComponent) {
        val.parentComponent = parentComponent;
      }
      onChange?.(val);
    },
    [onChange, showParentComponent],
  );

  const handleProductChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newProduct = event.target.value as string;
    setSelectedProduct(newProduct);
    setSelectedService('');
    setSelectedParentComponent('');
    setComponentOptions([]);
    setComponentError(null);
    emitChange(newProduct, '', '');
  };

  const handleServiceChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newService = event.target.value as string;
    setSelectedService(newService);
    setSelectedParentComponent('');
    emitChange(selectedProduct, newService, '');
  };

  const handleParentComponentChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newParent = event.target.value as string;
    setSelectedParentComponent(newParent);
    emitChange(selectedProduct, selectedService, newParent);
  };

  return (
    <>
      <FormControl
        margin="normal"
        required={required}
        error={required && !selectedProduct}
        fullWidth
      >
        <InputLabel id="product-select-label">Product</InputLabel>
        <Select
          labelId="product-select-label"
          value={selectedProduct}
          onChange={handleProductChange}
        >
          {options.map((product) => (
            <MenuItem key={product.value} value={product.value}>
              {product.label}
            </MenuItem>
          ))}
        </Select>
        {error && <FormHelperText error>{error}</FormHelperText>}
        {!error && <FormHelperText>Select a product from the list.</FormHelperText>}
      </FormControl>

      <FormControl
        margin="normal"
        required={serviceRequired}
        fullWidth
        error={!!serviceError || (serviceRequired && !selectedService && !!selectedProduct)}
      >
        <InputLabel id="service-select-label">Service</InputLabel>
        <Select
          labelId="service-select-label"
          value={selectedService}
          onChange={handleServiceChange}
          disabled={!selectedProduct || serviceOptions.length === 0}
        >
          {serviceOptions.map((service) => (
            <MenuItem key={service.value} value={service.value}>
              {service.label}
            </MenuItem>
          ))}
        </Select>
        {serviceError && <FormHelperText error>{serviceError}</FormHelperText>}
        {!serviceError && serviceRequired && !selectedService && selectedProduct && (
          <FormHelperText error>Service is required.</FormHelperText>
        )}
        {!serviceError && !(serviceRequired && !selectedService && selectedProduct) && (
          <FormHelperText>Select a service based on the product.</FormHelperText>
        )}
      </FormControl>

      {showParentComponent && selectedService && (
        <FormControl
          margin="normal"
          fullWidth
          required={parentComponentRequired}
          error={!!componentError || (parentComponentRequired && !selectedParentComponent)}
        >
          <InputLabel id="parent-component-select-label" shrink>
            Parent Component{parentComponentRequired ? '' : ' (Optional)'}
          </InputLabel>
          <Select
            labelId="parent-component-select-label"
            value={selectedParentComponent}
            onChange={handleParentComponentChange}
            disabled={componentLoading}
            displayEmpty
          >
            {!parentComponentRequired && (
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
            )}
            {componentOptions.map((comp) => (
              <MenuItem key={comp.value} value={comp.value}>
                {comp.label}
              </MenuItem>
            ))}
          </Select>
          {componentError && (
            <FormHelperText error>{componentError}</FormHelperText>
          )}
          {!componentError && parentComponentRequired && !selectedParentComponent && (
            <FormHelperText error>Parent Component is required.</FormHelperText>
          )}
          {!componentError && !(parentComponentRequired && !selectedParentComponent) && (
            <FormHelperText>
              {(() => {
                if (componentLoading) return 'Loading components...';
                if (componentOptions.length === 0) return 'No existing components in this system';
                if (parentComponentRequired) return 'Select a parent component for this subcomponent';
                return 'Select a parent component if this is a subcomponent';
              })()}
            </FormHelperText>
          )}
        </FormControl>
      )}
    </>
  );
};